import { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  Eye,
  EyeOff,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  userManagementAPI,
  type User as ServiceUser,
} from '@/services/user-management';
import type { PaginatedResponse } from '@/services/branchs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import type { AxiosError } from 'axios';
import { branchsAPI, type CompanyBranch } from '@/services/branchs';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { uploadFile } from '@/services/upload';

interface FormData {
  firstName: string;
  lastName: string;
  username: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  role: string;
  permissions: string[];
  status: 'active' | 'inactive';
  branchId: string;
  avatar?: string;
}

const availablePermissions = [
  {
    id: 'dashboard',
    label: 'View Dashboard',
    description: 'Access to main dashboard and analytics',
  },
  {
    id: 'products',
    label: 'Manage Products',
    description: 'Create, edit, and delete products',
  },
  {
    id: 'branches',
    label: 'Manage Branches',
    description: 'Create and manage store branches',
  },
  {
    id: 'users',
    label: 'Manage Users',
    description: 'Create and manage user accounts',
  },
  {
    id: 'orders',
    label: 'Manage Orders',
    description: 'View and process customer orders',
  },
  {
    id: 'reports',
    label: 'View Reports',
    description: 'Access to sales and analytics reports',
  },
  {
    id: 'settings',
    label: 'System Settings',
    description: 'Configure system-wide settings',
  },
  {
    id: 'integrations',
    label: 'Manage Integrations',
    description: 'Configure third-party integrations',
  },
];

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function extractErrors(error: unknown): string[] {
  const axiosError = error as AxiosError<unknown>;
  const payload = axiosError?.response?.data as unknown;

  const messages: string[] = [];

  if (!payload) {
    if (axiosError?.message) return [axiosError.message];
    return ['Failed to process request'];
  }

  if (typeof payload === 'string') {
    messages.push(payload);
  }

  if (isRecord(payload)) {
    const message = payload.message;
    if (typeof message === 'string') messages.push(message);
    if (Array.isArray(message)) {
      messages.push(
        ...message.filter((m): m is string => typeof m === 'string')
      );
    }

    const errorsField = payload.errors as unknown;
    if (Array.isArray(errorsField)) {
      messages.push(
        ...errorsField.filter((m): m is string => typeof m === 'string')
      );
    } else if (isRecord(errorsField)) {
      for (const key of Object.keys(errorsField)) {
        const value = errorsField[key];
        if (typeof value === 'string') messages.push(value);
        if (Array.isArray(value)) {
          messages.push(
            ...value.filter((m): m is string => typeof m === 'string')
          );
        }
      }
    }

    const details = payload.details as unknown;
    if (Array.isArray(details)) {
      for (const d of details) {
        if (typeof d === 'string') messages.push(d);
        else if (isRecord(d) && typeof d.message === 'string')
          messages.push(d.message);
      }
    }

    if (typeof payload.error === 'string') {
      messages.push(payload.error);
    }
  }

  if (messages.length > 0) return Array.from(new Set(messages));

  if (axiosError?.response?.statusText) return [axiosError.response.statusText];

  return ['Failed to process request'];
}

export function UserAddEdit() {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'manager',
    status: 'active',
    permissions: [],
    branchId: '',
    avatar: '',
  });

  const [submitErrors, setSubmitErrors] = useState<string[] | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Load branches for multi-select
  const { data: branchesResponse } = useQuery<
    PaginatedResponse<CompanyBranch>,
    Error
  >({
    queryKey: ['companyBranches'],
    queryFn: () => branchsAPI.getBranches(),
  });

  const companyBranches = branchesResponse?.data || [];

  // Set default branch when branches are loaded (for new users)
  useEffect(() => {
    if (!isEditMode && companyBranches.length > 0 && !formData.branchId) {
      setFormData((prev) => ({
        ...prev,
        branchId: companyBranches[0].id.toString(),
      }));
    }
  }, [companyBranches, isEditMode]);

  // Fetch user data in edit mode
  const { data: userData } = useQuery<ServiceUser, Error>({
    queryKey: ['user', id],
    queryFn: () => userManagementAPI.getUserById(id!),
    enabled: isEditMode,
  });

  // Update form data when user data is loaded (for edit mode)
  useEffect(() => {
    if (userData) {
      console.log(userData);
      setFormData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        username: userData.username,
        phoneNumber: userData.phoneNumber,
        password: '',
        confirmPassword: '',
        role: userData.roles[0] || 'manager',
        status:
          (userData as unknown as { status?: 'active' | 'inactive' }).status ||
          'active',
        permissions: userData.permissions.map((p) => p.id.toString()),
        branchId: userData?.branches[0]?.id?.toString() || '',
        avatar: userData.image ?? '',
      });
    }
  }, [userData]);

  const createUserMutation = useMutation({
    mutationFn: (user: FormData) => {
      return userManagementAPI.createUser({
        username: user.username,
        password: user.password,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        status: user.status,
        role: user.role,
        branchId: user.branchId ? Number(user.branchId) : undefined,
        image: user.avatar ?? null,
      });
    },
    onSuccess: () => {
      setSubmitErrors(null);
      toast.success('User created successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/user-management');
    },
    onError: (error: unknown) => {
      const errors = extractErrors(error);
      setSubmitErrors(errors);
      if (errors.length > 0) toast.error(errors[0]);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (user: FormData) => {
      const payload: Parameters<typeof userManagementAPI.updateUser>[1] = {
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        status: user.status,
        role: user.role,
        branchId: user.branchId ? Number(user.branchId) : undefined,
        image: user.avatar ?? null,
      };

      if (user.password) {
        payload.password = user.password;
      }

      return userManagementAPI.updateUser(id!, payload);
    },
    onSuccess: () => {
      setSubmitErrors(null);
      toast.success('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/user-management');
    },
    onError: (error: unknown) => {
      const errors = extractErrors(error);
      setSubmitErrors(errors);
      if (errors.length > 0) toast.error(errors[0]);
    },
  });

  const handleChange = (
    field: keyof FormData,
    value: FormData[keyof FormData]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter((p) => p !== permission),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErrors(null);

    // Phone: must start with + and digits
    const phoneRegex = /^\+\d{7,15}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setSubmitErrors([
        'Phone number must start with + and contain only digits',
      ]);
      return;
    }

    // Password: at least 1 letter and 1 number (when provided, or always on create)
    const needsPasswordValidation = !isEditMode || !!formData.password;
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).+$/;
    if (needsPasswordValidation && !passwordRegex.test(formData.password)) {
      setSubmitErrors(['Password must contain at least 1 letter and 1 number']);
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setSubmitErrors(['Passwords do not match']);
      return;
    }

    if (isEditMode) {
      updateUserMutation.mutate(formData);
    } else {
      createUserMutation.mutate(formData);
    }
  };

  const handleUploadAvatar = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        setIsUploading(true);
        const res = await uploadFile(file);
        setFormData((prev) => ({
          ...prev,
          avatar: `${res.url}`,
        }));
      } catch (error) {
        const [msg] = extractErrors(error);
        setSubmitErrors([msg || 'Failed to upload image']);
      } finally {
        setIsUploading(false);
      }
    };

    input.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <h1 className="text-xl md:text-2xl font-semibold">
              {isEditMode ? 'Edit User' : 'Add New User'}
            </h1>
            <p className="text-muted-foreground hidden md:block">
              {isEditMode
                ? 'Update user information'
                : 'Create a new user account'}
            </p>
          </div>
        </div>
      </div>

      {submitErrors && submitErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Failed to save user</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5 space-y-1">
              {submitErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex flex-col items-center gap-2 w-full mx-auto max-w-[8rem]">
                  <Avatar className="h-20 w-20">
                    <AvatarImage
                      src={
                        `${import.meta.env.VITE_API_STORAGE_URL}${formData.avatar}` ||
                        undefined
                      }
                    />
                    <AvatarFallback>
                      {getInitials(formData.firstName || 'User')}
                    </AvatarFallback>
                  </Avatar>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleUploadAvatar}
                    disabled={isUploading}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) =>
                        handleChange('firstName', e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    placeholder="+998991234567"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleChange('phoneNumber', e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Passwords */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Password{isEditMode ? ' (optional)' : ' *'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required={!isEditMode}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    Confirm Password{isEditMode ? ' (optional)' : ' *'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleChange('confirmPassword', e.target.value)
                      }
                      required={!isEditMode}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role and Access */}
          <Card>
            <CardHeader>
              <CardTitle>Role and Access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">User Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleChange('role', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch">Branch</Label>
                  <div className="relative">
                    <select
                      id="branch"
                      className="w-full h-9 appearance-none rounded-md border border-input bg-muted px-3 pr-8 text-sm text-foreground  focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                      value={formData.branchId}
                      onChange={(e) => handleChange('branchId', e.target.value)}
                    >
                      <option value="">Select branch</option>
                      {companyBranches.map((branch) => (
                        <option key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  </div>
                  {companyBranches.length === 0 && (
                    <span className="text-sm text-muted-foreground">
                      No branches found
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      handleChange('status', value as FormData['status'])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card className="hidden">
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
              <p className="text-sm text-muted-foreground">
                Select the permissions for this user role
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availablePermissions.map((permission) => (
                  <div
                    key={permission.id}
                    className="flex items-start space-x-3"
                  >
                    <Checkbox
                      id={permission.id}
                      checked={formData.permissions.includes(permission.id)}
                      onCheckedChange={(checked) =>
                        handlePermissionChange(
                          permission.id,
                          checked as boolean
                        )
                      }
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor={permission.id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {permission.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {permission.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Role Info */}
          <Card>
            <CardHeader>
              <CardTitle>Role Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Current Role
                </Label>
                <div className="mt-1">
                  <Badge
                    className={
                      formData.role === 'owner'
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                        : formData.role === 'admin'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                    }
                  >
                    {formData.role === 'owner'
                      ? 'Owner'
                      : formData.role === 'admin'
                        ? 'Admin'
                        : 'Manager'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Permissions Count
                </Label>
                <p className="mt-1">
                  {formData.permissions.length} of {availablePermissions.length}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">
                  Account Status
                </Label>
                <div className="mt-1">
                  <Badge
                    className={
                      formData.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                    }
                  >
                    {formData.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    createUserMutation.isPending || updateUserMutation.isPending
                  }
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update User' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(-1)}
                  disabled={
                    createUserMutation.isPending || updateUserMutation.isPending
                  }
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardHeader>
              <CardTitle>Role Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div>
                <span className="font-medium">Super Admin:</span> Full system
                access and control
              </div>
              <div>
                <span className="font-medium">Owner:</span> Business management
                and oversight
              </div>
              <div>
                <span className="font-medium">Branch Admin:</span>{' '}
                Branch-specific operations
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
